/**
 * Which language the caller is actually speaking (doc 30 §2).
 *
 * Pure, and deliberately narrow. It exists because of one observed failure: a
 * Polish business, a caller speaking Ukrainian, and a model that answered in
 * Polish, then mixed both inside single sentences ("Я zapraszam, Andrii,
 * wybrałeś…"), then drifted back to Polish for the availability answer and the
 * final confirmation. A prompt rule against exactly that was already in place
 * and lost, because the instructions themselves said "start in Polish", in
 * English, with Polish service names — the conversation and the system prompt
 * disagreed, and the system prompt kept winning.
 *
 * Two design choices worth stating.
 *
 * **Script first, words second.** The caller transcript comes from
 * `gpt-4o-mini-transcribe`, which on a real call rendered a Belarusian sentence
 * as "Я хочу ўнутршынім цянаў". Script survives that mangling; vocabulary
 * barely does. A Cyrillic sentence arriving at a Latin-script business is, on
 * its own, all the signal needed to know the model must stop using the business
 * language.
 *
 * **Silence over a guess.** Cyrillic with no distinguishing letter returns
 * `null` rather than the most likely language, and Latin script always returns
 * `null`: every language this product is configured in is Latin-script, and
 * there is no evidence of drift between two Latin-script languages. Locking the
 * wrong language would be worse than the drift it replaces.
 */
/** Minimum letters before a guess is allowed — "Так." must not switch a call. */
const MIN_LETTERS = 8;
const CYRILLIC = /[Ѐ-ӿ]/;
const LATIN = /[A-Za-z]/;
/**
 * An English language name for the prompt, or `null` when unsure.
 *
 * The name is English because it goes into instructions the model reads, not to
 * the caller.
 */
export function detectSpokenLanguage(text) {
    const letters = [...text].filter((ch) => CYRILLIC.test(ch) || LATIN.test(ch));
    if (letters.length < MIN_LETTERS)
        return null;
    const cyrillic = letters.filter((ch) => CYRILLIC.test(ch)).length;
    // A stray Latin character in a Cyrillic sentence (a service name read back, a
    // brand) must not tip the decision, and vice versa.
    if (cyrillic * 2 <= letters.length)
        return null;
    const lower = text.toLowerCase();
    // Letters that belong to exactly one of the three alphabets. Note what is NOT
    // here: ы and э are Belarusian as well as Russian (Андрэй, гэта), so neither
    // can stand in for "Russian" on its own.
    if (lower.includes('ў'))
        return 'Belarusian';
    if (/[їєґ]/.test(lower))
        return 'Ukrainian';
    if (lower.includes('ъ'))
        return 'Russian';
    // Then a few high-frequency words whose counterparts in the other two
    // languages are spelt differently (сегодня / сьогодні / сёння). Alphabet alone
    // is not enough: "На сегодня, на сегодня хочу" is plainly Russian and contains
    // none of the letters above.
    const words = new Set(lower.split(/[^\p{L}]+/u).filter(Boolean));
    // Two distinctive words in a substantive utterance, not a Cyrillic/name guess.
    const ukrainianWords = ['записати', 'записатися', 'записатись', 'свого', 'мого', 'собаку', 'хотіла', 'хотів', 'можеш'];
    if (words.size >= 4 && ukrainianWords.filter(w => words.has(w)).length >= 2)
        return 'Ukrainian';
    for (const [language, markers] of MARKER_WORDS) {
        if (markers.some((w) => words.has(w)))
            return language;
    }
    // Weakest signals last, and only as exclusions: і does not exist in Russian,
    // ы and э do not exist in Ukrainian. Belarusian shares both sides, so it is
    // only ever concluded from ў or a marker word above — which is the right way
    // round, since it is the rarest of the three on this product.
    if (lower.includes('і'))
        return 'Ukrainian';
    if (/[ыэ]/.test(lower))
        return 'Russian';
    return null;
}
/**
 * Which Latin-script language a sentence is in — English, Polish, German or
 * French — or `null` when unsure (doc 50 A3). Narrow on purpose, like the
 * Cyrillic detector: it counts DISTINCTIVE function words and letters, needs
 * two of them and a clear margin over the runner-up. A name, a "tak", "okay",
 * "super" or one English brand inside a Polish sentence never reaches that.
 *
 * Used only where the APPLICATION speaks in the caller's language (the Live
 * read-backs): the voice model follows the caller by itself, and the
 * application's sentences must not stay Polish while it does.
 */
export function detectLatinLanguage(text) {
    const letters = [...text].filter((ch) => /\p{L}/u.test(ch));
    if (letters.length < MIN_LATIN_LETTERS)
        return null;
    if (letters.filter((ch) => CYRILLIC.test(ch)).length * 2 > letters.length)
        return null;
    const lower = text.toLowerCase();
    const words = lower.split(/[^\p{L}'’]+/u).filter(Boolean);
    const scores = LATIN_MARKERS.map(([language, markers, letters]) => {
        let score = words.filter((w) => markers.has(w)).length;
        if (letters.test(lower))
            score += 2;
        return { language, score };
    }).sort((a, b) => b.score - a.score);
    const [best, second] = [scores[0], scores[1]];
    if (best.score < 2 || best.score < 2 * second.score)
        return null;
    return best.language;
}
const MIN_LATIN_LETTERS = 12;
/** Function words that are rare in the other three; letters only one of them uses. */
const LATIN_MARKERS = [
    [
        'English',
        new Set('the would like my you your is are can could please appointment tomorrow today cancel book have want what when this it with and of hello need there i’d i\'d i’m i\'m'.split(' ')),
        /(?!)/,
    ],
    [
        'Polish',
        new Set('jest nie się chcę chciałbym chciałabym proszę jutro dzisiaj czy jak mam że wizytę wizyty godzinie dzień dobry odwołać mogę może jeszcze bardzo na w z co'.split(' ')),
        /[ąćęłńśźż]/,
    ],
    ['German', new Set('ich nicht und der die das ist bitte möchte morgen einen eine haben kann wie mit für guten heute'.split(' ')), /[äöüß]/],
    ['French', new Set('je voudrais le la les est pas et un une demain rendez vous pour avec bonjour aujourd’hui aujourd\'hui mon oui annuler'.split(' ')), /[àâçèêëîïôûùœ]/],
];
/**
 * Word markers, checked in this order. Only words that differ across all three
 * languages are listed — one that Ukrainian and Russian share says nothing, and
 * guessing from it would be worse than the `null` it replaces.
 */
const MARKER_WORDS = [
    ['Belarusian', ['што', 'гэта', 'сёння', 'дзякуй', 'добры', 'мяне', 'завуць', 'можаш']],
    ['Ukrainian', ['що', 'це', 'сьогодні', 'дуже', 'дякую', 'ласка', 'добрий', 'мене', 'можеш']],
    ['Russian', ['что', 'это', 'сегодня', 'очень', 'сейчас', 'спасибо', 'можешь', 'меня', 'пожалуйста']],
];
// Deliberately absent, having failed the "differs across all three" test:
// `ці` is Belarusian "whether" but also Ukrainian "these" ("ці послуги"), and
// `зараз` means "now" in both Belarusian and Ukrainian. Either one would have
// answered a Ukrainian caller in Belarusian for the rest of the call.
/**
 * An EXPLICIT request for a language — "can you speak Ukrainian?", "po polsku
 * proszę", "auf Deutsch bitte" (doc 33 W11 #7). Returns the English name.
 *
 * Deliberately a phrase list, not language identification: it recognises a
 * caller ASKING, which is the strongest signal there is, and it does so in the
 * caller's words in any of the languages we have seen. It does not claim to
 * identify what language a sentence is in — `detectSpokenLanguage` remains the
 * (narrow, Cyrillic-only) heuristic for that.
 *
 * The doc 31 §8 call is the reason it exists: "А ты можаш размаўляць
 * па-ўкраінску?" is a Belarusian sentence asking for UKRAINIAN. Script
 * detection says Belarusian; the caller said Ukrainian.
 *
 * Naming a language is not always asking for it — see `classifyLanguageMention`.
 */
export function detectLanguageRequest(text) {
    return classifyLanguageMention(text)?.requested ?? null;
}
/**
 * Every language a sentence names, each read as a request or a REJECTION
 * (2026-10-10). "Czemu ty mówisz po ukraińsku?" — "why are you speaking
 * Ukrainian?" — was matched by the phrase list as a request FOR Ukrainian, from
 * a caller who had spoken Polish the whole call and was asking the assistant to
 * stop.
 *
 * A mention is a rejection when its own clause asks WHY the assistant speaks it
 * ("why are you speaking X", "чому ти говориш X") or negates it ("nie mów po X",
 * "I don't speak X", "не розумію X", "nie po X"). Two negated forms stay
 * requests, because they are how people ask: "why DON'T you speak X?" and
 * "can't you speak X?". Everything else that names a language is a request, as
 * before. `null` when no language is named.
 */
export function classifyLanguageMention(text) {
    const t = text.toLowerCase().replace(/[’`]/g, "'");
    const found = [];
    for (const [language, patterns] of LANGUAGE_REQUESTS) {
        const at = Math.min(...patterns.map((re) => re.exec(t)?.index ?? Infinity));
        if (at !== Infinity)
            found.push({ language, at });
    }
    if (found.length === 0)
        return null;
    found.sort((a, b) => a.at - b.at);
    let requested = null;
    const rejected = [];
    for (const { language, at } of found) {
        if (rejects(clauseBefore(t, at)))
            rejected.push(language);
        else
            requested ??= language;
    }
    return { requested, rejected };
}
/**
 * The language the caller asked for — named, or implied by refusing another:
 * "Czemu ty mówisz po ukraińsku?" asks for the language the question itself is
 * in. `null` when nothing was asked, or the question's language is unclear.
 */
export function askedLanguage(text) {
    const mention = classifyLanguageMention(text);
    if (!mention)
        return null;
    if (mention.requested)
        return mention.requested;
    const own = detectSpokenLanguage(text) ?? detectLatinLanguage(text);
    return own && !mention.rejected.includes(own) ? own : null;
}
/** The words between the previous clause break and a language mention. */
function clauseBefore(t, at) {
    let start = 0;
    for (const m of t.slice(0, at).matchAll(CLAUSE_BREAK))
        start = (m.index ?? 0) + m[0].length;
    return t.slice(start, at);
}
function rejects(clause) {
    const why = WHY.exec(clause);
    // "Why are you speaking X?" asks to stop; "why DON'T you speak X?" asks for it.
    if (why)
        return !NEGATION.test(clause.slice(why.index + why[0].length));
    if (NEGATED_QUESTION.test(clause))
        return false;
    return NEGATION.test(clause);
}
const CLAUSE_BREAK = /[,.;:!?]|(?<!\p{L})(?:tylko|ale|lecz|but|instead|rather|а|але|лише|тільки|только|но|sondern|aber|mais|plutôt)(?!\p{L})/gu;
const WHY = /(?<!\p{L})(?:czemu|dlaczego|why|чому|навіщо|нащо|почему|зачем|warum|wieso|pourquoi)(?!\p{L})/u;
const NEGATION = /(?<!\p{L})(?:nie|не|ні|not|never|nicht|kein|keine|ne|pas)(?!\p{L})|n't(?!\p{L})/u;
/** "Can't you…?", "czy nie może pan…?", "чи не могли б…?" — asking, negated. */
const NEGATED_QUESTION = /(?<!\p{L})(?:(?:can't|cannot|couldn't|won't|wouldn't) (?:you|we)|czy nie|чи не|не могли б|нельзя ли|не могли бы|kannst du nicht|können sie nicht|ne pouvez-vous)(?!\p{L})/u;
const LANGUAGE_REQUESTS = [
    ['Ukrainian', [/па-ўкраін/, /по-украї/, /українськ/, /украинск/, /по-украин/, /\bukrainian\b/, /po ukraińsku/, /\bukraińsk/, /auf ukrainisch/, /en ukrainien/]],
    ['Belarusian', [/па-беларуск/, /по-белорусск/, /беларуск(ай|ую) мов/, /\bbelarusian\b/, /po białorusku/]],
    ['Russian', [/по-русски/, /на русском/, /русск(ий|ом) язык/, /\brussian\b/, /po rosyjsku/, /auf russisch/, /en russe/]],
    ['English', [/\bin english\b/, /\bspeak english\b/, /\benglish,? please\b/, /po angielsku/, /auf englisch/, /en anglais/, /по-английски/, /англійськ/]],
    ['Polish', [/\bpo polsku\b/, /\bin polish\b/, /по-польски/, /польськ(ою|ій)/, /auf polnisch/, /en polonais/]],
    ['German', [/\bauf deutsch\b/, /\bin german\b/, /\bpo niemiecku\b/, /по-немецки/, /німецьк/, /en allemand/]],
    ['French', [/\ben fran[cç]ais\b/, /\bin french\b/, /\bpo francusku\b/, /по-французски/, /французьк/, /auf französisch/]],
];
/** How many times one call may be re-instructed for a language (doc 30 §2). */
export const MAX_LANGUAGE_LOCKS = 2;
/** Explicit requests get their own, slightly larger, bound. */
export const MAX_EXPLICIT_LOCKS = 3;
const LANGUAGE_NAMES = { fr: 'French', de: 'German', en: 'English', pl: 'Polish' };
/** ISO 639-1 for a language name this module produces (transcription, TTS). */
export const LANGUAGE_CODE = {
    English: 'en',
    Polish: 'pl',
    German: 'de',
    French: 'fr',
    Ukrainian: 'uk',
    Russian: 'ru',
    Belarusian: 'be',
};
/**
 * Which language the session is instructed in (doc 30 §2, doc 31 §8, doc 33
 * W11 #7). The configured language opens the call; after that:
 *
 * 1. An EXPLICIT request switches immediately and becomes the preference.
 *    Once a caller has asked for a language, incidental sentences (a name in
 *    Belarusian, one Russian phrase) no longer move the call — only another
 *    explicit request does.
 * 2. Without one, the script heuristic applies: the first switch away from the
 *    business language is immediate, leaving a locked language takes two
 *    consecutive sentences.
 *
 * Bounded either way: a detector that flapped must not re-instruct the session
 * on every utterance.
 */
export class LanguagePolicy {
    businessLanguage;
    opts;
    spokenLanguage = null;
    explicit = null;
    locks = 0;
    explicitLocks = 0;
    pending = null;
    pendingTurn = null;
    /** `latin`: also follow Latin-script languages (doc 50 A3) — where the
     * application itself speaks to the caller (Live). */
    constructor(businessLanguage, opts = {}) {
        this.businessLanguage = businessLanguage;
        this.opts = opts;
    }
    get spoken() {
        return this.spokenLanguage;
    }
    /** Returns the newly locked language when the session must be re-instructed. */
    observe(text, turn) {
        const mention = classifyLanguageMention(text);
        const requested = mention ? askedLanguage(text) : null;
        // A refusal whose own language is unclear ("Чому польською?"): it says what
        // NOT to speak, never what to speak instead.
        if (mention && !requested)
            return null;
        if (requested) {
            if (requested === this.explicit || this.explicitLocks >= MAX_EXPLICIT_LOCKS)
                return null;
            this.explicit = requested;
            this.pending = null;
            // Asking for the business language while not switched: nothing to change.
            if (this.spokenLanguage === null && requested === LANGUAGE_NAMES[this.businessLanguage])
                return null;
            if (requested === this.spokenLanguage)
                return null;
            this.explicitLocks += 1;
            this.spokenLanguage = requested;
            return requested;
        }
        if (this.explicit)
            return null;
        if (this.locks >= MAX_LANGUAGE_LOCKS)
            return null;
        const spoken = detectSpokenLanguage(text) ?? (this.opts.latin ? detectLatinLanguage(text) : null);
        if (!spoken)
            return null;
        if (spoken === this.spokenLanguage) {
            this.pending = null;
            return null;
        }
        if (this.spokenLanguage !== null && (this.pending !== spoken || (turn !== undefined && this.pendingTurn === turn))) {
            this.pending = spoken;
            this.pendingTurn = turn ?? null;
            return null;
        }
        this.pending = null;
        if (spoken === LANGUAGE_NAMES[this.businessLanguage]) {
            // Back to the business language (reachable only with `latin`): the
            // application speaks it again rather than staying in the old one.
            if (this.spokenLanguage === null)
                return null;
            this.spokenLanguage = null;
            this.locks += 1;
            return spoken;
        }
        this.spokenLanguage = spoken;
        this.locks += 1;
        return spoken;
    }
}
/** At most this many language corrections per call. */
export const MAX_DRIFT_CORRECTIONS = 3;
const CYRILLIC_LANGUAGES = new Set(['Ukrainian', 'Russian', 'Belarusian']);
/**
 * Notices the MODEL answering in a language the caller is not speaking
 * (2026-10-10). `LanguagePolicy` follows the caller; nothing followed the model.
 *
 * On a browser test call, a caller speaking Polish with a Ukrainian accent was
 * answered in Ukrainian, then, after asking "Czemu ty mówisz po ukraińsku?"
 * twice, in English ("I switched to the language you used in your last
 * message"), although every transcript of their words was Polish. The voice
 * model picks its reply language from the audio, and an accent is in the audio;
 * the transcriber, which only writes the words down, had it right every time.
 *
 * So the words decide. When the caller's latest turn is clearly in one language
 * (or asked for one) and the model's reply is clearly in another the caller did
 * not ask for, `reply` returns the drift to correct. Narrow like the
 * detectors under it — no correction for an unclear sentence on either side, a
 * second one for the same caller words, or more than `MAX_DRIFT_CORRECTIONS` per
 * call — and it stays out of Ukrainian/Russian/Belarusian: transcripts blur
 * those three, and telling a Ukrainian caller's assistant to speak Russian is
 * worse than the drift it would fix. `LanguagePolicy` owns that distinction.
 */
export class LanguageDriftGuard {
    /** What the caller said last, and the language it is clearly in. */
    heard = '';
    latest = null;
    /** The language the caller last asked for, explicitly or by refusing another. */
    requested = null;
    /** A caller turn was committed and its words have not arrived yet. */
    awaitingWords = false;
    /** A reply that arrived before the words it answers. */
    heldReply = null;
    correctedFor = null;
    corrections = 0;
    /**
     * A caller turn was committed; its transcript is still on its way. The reply
     * to it is often transcribed first, and must be judged against THESE words,
     * not the previous turn's.
     */
    callerTurn() {
        this.awaitingWords = true;
        this.heldReply = null;
    }
    /**
     * The caller's latest turn, whole (call again as it grows; `''` for a turn
     * whose transcription failed). Returns a drift when a reply held for these
     * words turns out to be in the wrong language.
     */
    caller(text) {
        this.awaitingWords = false;
        const t = text.trim();
        if (!t) {
            // Nothing known about what the reply answered: never judge it against
            // the turn before.
            this.heldReply = null;
            return null;
        }
        if (t !== this.heard) {
            this.heard = t;
            if (classifyLanguageMention(t)) {
                const asked = askedLanguage(t);
                if (asked)
                    this.requested = asked;
                this.latest = asked;
            }
            else {
                this.latest = detectSpokenLanguage(t) ?? detectLatinLanguage(t);
            }
        }
        const held = this.heldReply;
        this.heldReply = null;
        return held ? this.reply(held) : null;
    }
    /** The model's reply so far: a drift to correct, or `null`. */
    reply(text) {
        if (this.awaitingWords) {
            this.heldReply = text;
            return null;
        }
        const expected = this.latest ?? this.requested;
        if (!expected || this.correctedFor === this.heard || this.corrections >= MAX_DRIFT_CORRECTIONS)
            return null;
        const said = detectSpokenLanguage(text) ?? detectLatinLanguage(text);
        if (!said || said === expected || said === this.requested)
            return null;
        if (CYRILLIC_LANGUAGES.has(said) && CYRILLIC_LANGUAGES.has(expected))
            return null;
        this.corrections += 1;
        this.correctedFor = this.heard;
        return { expected, said, instruction: languageCorrection(expected, said, this.heard) };
    }
}
/** The instruction a drift correction gives the model (in English, like the rest of its instructions). */
export function languageCorrection(expected, said, heard) {
    const quote = heard.replace(/[«»\n\r]+/g, ' ').trim().slice(0, 160);
    return (`Language correction: the caller is speaking ${expected} — their last words were «${quote}» — but you just answered in ${said}. ` +
        `From your next sentence on, speak ONLY ${expected}, the language of the caller's WORDS; an accent, a voice or a name never decides the language. ` +
        `If they asked why you changed language, say sorry in a few words, in ${expected}, and carry on. Never move to a third language.`);
}
//# sourceMappingURL=language.js.map